import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { Button } from "@/components/ui/button.jsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.jsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog.jsx";
import { X, Check } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast.js";

export default function FinancingSolutions() {
  const { toast } = useToast();
  const [selectedReason, setSelectedReason] = useState(null);
  const [demandesEnvoyees, setDemandesEnvoyees] = useState([]);

  const raisonsIneligibilite = {
    KARMEN: ["Code NAF non éligible pour ce type de financement"],
    PRETUP: [
      "ATD présentes sur les comptes bancaires",
      "Procédure collective de moins de 10 ans",
    ],
    AIRPME: ["Moins de 3 ans d'activité", "Chiffre d'affaires insuffisant"],
  };

  const handleEnvoyerDemande = (financeur) => {
    setDemandesEnvoyees((prev) => [...prev, financeur]);
    toast({
      title: "Demande envoyée",
      description: `Suivez les statuts de la demande sur app.leano`,
    });
  };
  return (
    <div className="p-6 space-y-6">
      <FinancingSolutionsHeader />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
              <div>Financeur</div>
              <div>Statut</div>
              <div>Action</div>
            </div>

            {["DEFACTO", "AVANSEO", "RIVERBANK"].map((partner) => (
              <FinancingSolutionsEligible
              key={partner}
                partner={partner}
                demandesEnvoyees={demandesEnvoyees}
                handleEnvoyerDemande={handleEnvoyerDemande}
              />
            ))}

            {["KARMEN", "PRETUP", "AIRPME"].map((partner) => (
              <FinancingSolutionsIneligible
              key={partner}
                partner={partner}
                raisonsIneligibilite={raisonsIneligibilite}
                setSelectedReason={setSelectedReason}
              />
            ))}
          </div>

          {/* Bouton refuser le dossier */}
          <div className="mt-8 pt-6 border-t">
            <div className="flex justify-center">
              <Button variant="destructive" className="flex items-center gap-2">
                <X className="h-4 w-4" />
                Refuser le dossier
              </Button>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-2">
              Le dossier ne sera envoyé à aucun financeur
            </p>
          </div>
        </CardContent>
      </Card>

      <FinancingSolutionsIneligibleDialog
        selectedReason={selectedReason}
        setSelectedReason={setSelectedReason}
      />
    </div>
  );
}

function FinancingSolutionsEligible({
  demandesEnvoyees,
  partner,
  handleEnvoyerDemande,
}) {
  return (
    <div className="grid grid-cols-3 gap-4 items-center py-3 border-b">
      <div className="font-medium">{partner}</div>
      <div>
        <Badge
          variant="default"
          className="bg-green-100 text-green-800 hover:bg-green-100"
        >
          Éligible
        </Badge>
      </div>
      <div>
        {demandesEnvoyees.includes(partner) ? (
          <Button
            size="sm"
            className="text-xs bg-green-600 hover:bg-green-600"
            disabled
          >
            <Check className="h-3 w-3 mr-1" />
            Envoyée
          </Button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" className="text-xs">
                Envoyer la demande
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmer l'envoi</AlertDialogTitle>
                <AlertDialogDescription>
                  Êtes-vous sûr d'envoyer la demande au financeur
                  {partner} ?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Non</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleEnvoyerDemande(partner)}
                >
                  Oui
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

function FinancingSolutionsIneligible({
  partner,
  raisonsIneligibilite,
  setSelectedReason,
}) {
  return (
    <div className="grid grid-cols-3 gap-4 items-center py-3 border-b">
      <div className="font-medium">{partner}</div>
      <div>
        <Badge variant="destructive">Pas éligible</Badge>
      </div>
      <div>
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={() =>
            setSelectedReason({
              financeur: partner,
              raisons: raisonsIneligibilite[partner],
            })
          }
        >
          Voir les raisons
        </Button>
      </div>
    </div>
  );
}

function FinancingSolutionsHeader() {
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">
        Solutions de financement
      </h1>
      <p className="text-muted-foreground mt-2">
        Éligibilité aux différentes solutions de financement
      </p>
    </div>
  );
}

function FinancingSolutionsIneligibleDialog({
  selectedReason,
  setSelectedReason,
}) {
  return (
    <Dialog
      open={!!selectedReason}
      onOpenChange={() => setSelectedReason(null)}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Raisons d'inéligibilité - {selectedReason?.financeur}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {selectedReason?.raisons.map((raison, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-muted-foreground">{raison}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
